/**
 * Search Service - API client for unified search
 * TASK-270: Unified Search Interface
 *
 * Features:
 * - Request cancellation with AbortController
 * - Retry logic with exponential backoff
 * - Response caching
 * - WebSocket streaming support
 */

import { API_CONFIG } from '../config/api-config';
import type {
  SearchRequest,
  SearchResponse,
  StreamingChunk,
  SearchStatus,
} from '../types/search.types';

/**
 * Retry configuration
 */
interface RetryOptions {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
};

/** Auto-timeout for search requests.
 *  TASK-400 C6.5: overridable via VITE_SEARCH_TIMEOUT_MS for environments with
 *  slower downstream services (cold-start, shared dev stacks). Defaults to 45s
 *  because text-to-SQL + LLM synthesis consistently takes 17-30s in dev. */
const SEARCH_TIMEOUT_MS = Number(import.meta.env.VITE_SEARCH_TIMEOUT_MS) || 45_000;

/**
 * Execute a function with exponential backoff retry
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {},
  signal?: AbortSignal
): Promise<T> {
  const { maxRetries, baseDelay, maxDelay } = { ...DEFAULT_RETRY_OPTIONS, ...options };

  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Check if aborted before attempt
    if (signal?.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }

    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry on abort
      if (lastError.name === 'AbortError') {
        throw lastError;
      }

      // Don't retry on client errors (4xx)
      if ((error as any)?.status >= 400 && (error as any)?.status < 500) {
        throw lastError;
      }

      // Last attempt - throw the error
      if (attempt === maxRetries) {
        throw lastError;
      }

      // Calculate delay with exponential backoff and jitter
      const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
      const jitter = delay * 0.2 * Math.random();
      const totalDelay = delay + jitter;

      // Wait before retry
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(resolve, totalDelay);

        // Handle abort during wait
        if (signal) {
          signal.addEventListener('abort', () => {
            clearTimeout(timeoutId);
            reject(new DOMException('Request aborted', 'AbortError'));
          }, { once: true });
        }
      });
    }
  }

  throw lastError || new Error('Retry failed');
}

// Transform snake_case response to camelCase
function transformResponse(data: Record<string, unknown>): SearchResponse {
  return {
    answer: data.answer as string,
    status: data.status as SearchStatus,
    insights: (data.insights as Array<Record<string, unknown>> || []).map((i) => ({
      title: i.title as string,
      description: i.description as string,
      category: i.category as string,
      importance: i.importance as string,
      confidence: i.confidence as number,
      sourceIds: i.source_ids as string[] || [],
    })),
    sources: (data.sources as Array<Record<string, unknown>> || []).map((s) => ({
      sourceType: s.source_type as string,
      sourceId: s.source_id as string,
      title: s.title as string | undefined,
      excerpt: s.excerpt as string,
      relevanceScore: s.relevance_score as number,
      timestamp: s.timestamp as string | undefined,
      link: s.link as string | undefined,
      metadata: s.metadata as Record<string, unknown> | undefined,
    })),
    toolResults: (data.tool_results as Array<Record<string, unknown>> || []).map((t) => ({
      toolName: t.tool_name as string,
      sourceType: t.source_type as string,
      success: t.success as boolean,
      data: t.data,
      error: t.error as string | undefined,
      durationMs: t.duration_ms as number,
      sources: (t.sources as Array<Record<string, unknown>> || []).map((s) => ({
        sourceType: s.source_type as string,
        sourceId: s.source_id as string,
        title: s.title as string | undefined,
        excerpt: s.excerpt as string,
        relevanceScore: s.relevance_score as number,
        timestamp: s.timestamp as string | undefined,
        link: s.link as string | undefined,
        metadata: s.metadata as Record<string, unknown> | undefined,
      })),
    })),
    query: data.query as string,
    intent: data.intent as string,
    confidence: data.confidence as number,
    queryTimeMs: data.query_time_ms as number,
    failedSources: data.failed_sources as string[] || [],
    warnings: data.warnings as string[] || [],
    followUpSuggestions: data.follow_up_suggestions as string[] || [],
  } as SearchResponse;
}

class SearchService {
  private baseUrl: string;
  private wsUrl: string;
  private cache: Map<string, { response: SearchResponse; timestamp: number }>;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.baseUrl = API_CONFIG.GATEWAY_URL;
    this.wsUrl = this.baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    this.cache = new Map();
  }

  /**
   * Execute a search query with optional abort signal and automatic retry
   *
   * @param request - Search request parameters
   * @param signal - Optional AbortSignal for request cancellation
   * @param retryOptions - Optional retry configuration
   * @returns Search response
   *
   * @example
   * // Basic usage
   * const response = await searchService.search({ query: 'find client' });
   *
   * // With abort controller
   * const controller = new AbortController();
   * const response = await searchService.search({ query: 'find client' }, controller.signal);
   * // Cancel if needed: controller.abort();
   */
  async search(
    request: SearchRequest,
    signal?: AbortSignal,
    retryOptions?: Partial<RetryOptions>
  ): Promise<SearchResponse> {
    // Check cache first (skip if aborted)
    if (!signal?.aborted) {
      const cacheKey = JSON.stringify(request);
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.response;
      }
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Authentication required');
    }

    // Use retry wrapper for resilience
    // Create a timeout AbortController that wraps any user-provided signal
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS);

    // TASK-400 C6.4: hoist the abort handler to a named ref so we can remove it
    // in `finally`. The previous inline arrow with `{ once: true }` leaked the
    // listener if the fetch resolved without aborting — the signal stayed
    // subscribed to our disposed controller until the caller's signal was GC'd.
    const abortHandler = () => controller.abort();
    if (signal) {
      if (signal.aborted) {
        clearTimeout(timeoutId);
        throw new DOMException('Request aborted', 'AbortError');
      }
      signal.addEventListener('abort', abortHandler);
    }

    const combinedSignal = controller.signal;

    try {
      return await withRetry(
        async () => {
          const response = await fetch(`${this.baseUrl}/api/search/query`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              query: request.query,
              sources: request.sources,
              date_from: request.dateFrom,
              date_to: request.dateTo,
              limit: request.limit || 10,
              include_sources: request.includeSources ?? true,
              // TASK-400: Context for fast-path routing
              client_id: request.clientId,
              entity_type: request.entityType,
              entity_id: request.entityId,
              context_hints: request.contextHints,
            }),
            signal: combinedSignal,
          });

          if (!response.ok) {
            if (response.status >= 400 && response.status < 500) {
              const body = await response.json().catch(() => ({ detail: 'Search failed' }));
              throw Object.assign(new Error(body.detail || 'Search failed'), { status: response.status });
            }
            // Server errors will be retried
            throw new Error(`Server error: ${response.status}`);
          }

          const data = await response.json();
          const result = transformResponse(data);

          // Cache successful result
          const cacheKey = JSON.stringify(request);
          this.cache.set(cacheKey, { response: result, timestamp: Date.now() });

          return result;
        },
        retryOptions,
        combinedSignal
      );
    } catch (error) {
      // Distinguish timeout abort from user-initiated cancel
      if (error instanceof DOMException && error.name === 'AbortError') {
        if (signal?.aborted) {
          // Re-throw as AbortError so callers (searchExecutor) can detect cancellation
          throw error;
        }
        // Timeout — throw a descriptive Error
        throw new Error(`Search timed out after ${SEARCH_TIMEOUT_MS / 1000}s`);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      // TASK-400 C6.4: always detach the abort listener from the user-provided
      // signal. Harmless if it was never attached (no signal passed).
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }
    }
  }

  /**
   * Execute a streaming search query
   */
  searchStream(
    request: SearchRequest,
    callbacks: {
      onToken?: (content: string) => void;
      onSource?: (source: SearchResponse['sources'][0]) => void;
      onStatus?: (status: SearchStatus) => void;
      onDone?: (metadata: Record<string, unknown>) => void;
      onError?: (error: string) => void;
    }
  ): () => void {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      callbacks.onError?.('Authentication required');
      return () => {};
    }

    // TASK-568: browsers can't send an Authorization header on a WebSocket, so pass
    // the JWT as a query param (the gateway /ws/search handler reads it). Matches the
    // /ws/chat + /ws/alerts pattern.
    const ws = new WebSocket(`${this.wsUrl}/ws/search?token=${encodeURIComponent(token)}`);
    let isOpen = false;
    let settled = false; // a terminal 'done'/'error'/close has already been delivered

    ws.onopen = () => {
      isOpen = true;
      ws.send(
        JSON.stringify({
          type: 'query',
          query: request.query,
          sources: request.sources,
          limit: request.limit || 10,
          // TASK-400: Context for fast-path routing
          client_id: request.clientId,
          entity_type: request.entityType,
          entity_id: request.entityId,
          context_hints: request.contextHints,
        })
      );
    };

    ws.onmessage = (event) => {
      try {
        const chunk: StreamingChunk = JSON.parse(event.data);

        switch (chunk.type) {
          case 'token':
            callbacks.onToken?.(chunk.content || '');
            break;
          case 'source':
            if (chunk.source) {
              callbacks.onSource?.(chunk.source);
            }
            break;
          case 'status':
            if (chunk.status) {
              callbacks.onStatus?.(chunk.status);
            }
            break;
          case 'done':
            settled = true;
            callbacks.onDone?.(chunk.metadata || {});
            ws.close();
            break;
          case 'error':
            settled = true;
            callbacks.onError?.(chunk.content || 'Unknown error');
            break;
        }
      } catch (e) {
        console.error('Failed to parse streaming chunk:', e);
      }
    };

    ws.onerror = () => {
      if (settled) return;
      settled = true;
      callbacks.onError?.('WebSocket connection error');
    };

    ws.onclose = (event) => {
      isOpen = false;
      // An abnormal close before any result (e.g. gateway 4001 auth close) fires
      // onclose, NOT onerror — surface it so SearchPage can fall back to REST.
      // 1000 = normal (we close after 'done'). (TASK-568)
      if (!settled && event.code !== 1000) {
        settled = true;
        callbacks.onError?.(`WebSocket closed (${event.code})`);
      }
    };

    // Return cleanup function
    return () => {
      if (isOpen) {
        ws.close();
      }
    };
  }

  /**
   * Get search system health
   */
  async getHealth(): Promise<{ status: string; enabled: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/search/health`);
    return response.json();
  }

  /**
   * Clear search cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const searchService = new SearchService();
export default searchService;
